import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../constants";
import { useAuth } from "../../../../useAuth";
import { getLandholdingDashboardRoute, getProjectDetailsByUuid, getProjectsRoute } from "../../../../utils/routes";
import { Toast } from "../../../../widget";
import { NewProjectJourneyCompletion } from "./NewProjectJourneyContext";

const REDIRECT_DELAY_SECONDS = 3;

const countOf = (count: number, singular: string): string => `${count} ${count === 1 ? singular : `${singular}s`}`;

const getCreatedMessage = (isLandholding: boolean, projectCount: number, invitationCount: number): string => {
  const projects = countOf(projectCount, "project");
  let created: string;

  if (!isLandholding) created = `${projects} created successfully.`;
  else if (projectCount === 0) created = "Landholding created successfully.";
  else created = `Landholding and ${projects} created successfully.`;

  return invitationCount > 0 ? `${created} ${countOf(invitationCount, "invitation")} sent.` : created;
};

// Null when there is nowhere to land: asset managers have no projects list yet.
const getDestination = (
  projectUuidByKey: NewProjectJourneyCompletion["projectUuidByKey"],
  groupUuid: string | undefined,
  userType: OrganisationTypeConstants
): string | null => {
  const projectUuids = Object.values(projectUuidByKey);

  if (groupUuid) return getLandholdingDashboardRoute(groupUuid, userType);
  if (projectUuids.length === 1) return getProjectDetailsByUuid(projectUuids[0], userType);
  if (userType === OrganisationTypeConstants.ASSET_MANAGER) return null;
  return getProjectsRoute(userType);
};

interface UseNewProjectJourneyPageReturnData {
  pageTitle: string;
  /** Seed (asset manager) renders inside the Seed chrome; the hubs inside the developer chrome. */
  isSeed: boolean;
  journeyKey: number;
  handleComplete: (completion: NewProjectJourneyCompletion) => void;
}

export const useNewProjectJourneyPage = (): UseNewProjectJourneyPageReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  // Bumped to remount the journey fresh when there is nowhere to redirect to.
  const [journeyKey, setJourneyKey] = useState(0);
  const redirectTimeoutRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(redirectTimeoutRef.current), []);

  const handleComplete = useCallback(
    ({ projectUuidByKey, groupUuid, projectCount, invitationCount, isLandholding }: NewProjectJourneyCompletion) => {
      Toast.success({
        message: getCreatedMessage(isLandholding || !!groupUuid, projectCount, invitationCount),
        duration: REDIRECT_DELAY_SECONDS,
      });

      const destination = getDestination(projectUuidByKey, groupUuid, currentUserType as OrganisationTypeConstants);

      redirectTimeoutRef.current = window.setTimeout(() => {
        if (destination) navigate(destination);
        else setJourneyKey((key) => key + 1);
      }, REDIRECT_DELAY_SECONDS * 1000);
    },
    [currentUserType, navigate]
  );

  return {
    pageTitle: "New project",
    isSeed: currentUserType === OrganisationTypeConstants.ASSET_MANAGER,
    journeyKey,
    handleComplete,
  };
};
